import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Chip,
  Collapse,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TableSortLabel,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFileOutlined,
  DeleteOutlined,
  VisibilityOutlined,
  DownloadOutlined,
  Close,
  OpenInNew,
  CreateNewFolder,
  Search,
  FolderOpen,
  Folder,
  ExpandMore,
  ExpandLess,
  Add,
  MergeType,
  ArrowUpward,
  ArrowDownward,
  Edit,
  DriveFileMove,
  Chat,
  VerifiedUser,
  ShareOutlined,
  PersonAdd,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../hooks/useAuth';
import proceedingService from '../services/proceedingService';
import { documentService } from '../../documents';
import DocumentEditForm from '../../documents/components/DocumentEditForm';
import DocumentSignatures from './DocumentSignatures';
import useSortTable from '../../../hooks/useSortTable';
import AiChatDialog from '../../../components/AiChatDialog';
import EntityModalForm from '../../entities/components/EntityModalForm';
import entityService from '../../entities/services/entityService';
import { companyService } from '../../companies';
import { extractErrorMessage } from '../../../utils/errorUtils';

const NATIVE_IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'];
const PDF_TYPES = ['pdf'];
const SPREADSHEET_TYPES = ['xls', 'xlsx', 'csv'];
const VIDEO_TYPES = ['mp4', 'webm', 'ogg', 'mov'];
const AUDIO_TYPES = ['mp3', 'wav', 'aac', 'flac'];
const PREVIEWABLE_TYPES = [...NATIVE_IMAGE_TYPES, ...PDF_TYPES, ...SPREADSHEET_TYPES, ...VIDEO_TYPES, ...AUDIO_TYPES];

const getFileExt = (filename) => {
  if (!filename) return '';
  return filename.split('.').pop().toLowerCase();
};

const getDocExt = (doc) => {
  return [doc.file_original_name, doc.name, doc.file]
    .filter(Boolean)
    .map(getFileExt)
    .find(ext => ext) || '';
};

const isPreviewable = (doc) => {
  return PREVIEWABLE_TYPES.includes(getDocExt(doc));
};

const isImage = (doc) => NATIVE_IMAGE_TYPES.includes(getDocExt(doc));
const isSpreadsheet = (doc) => SPREADSHEET_TYPES.includes(getDocExt(doc));
const isVideo = (doc) => VIDEO_TYPES.includes(getDocExt(doc));
const isAudio = (doc) => AUDIO_TYPES.includes(getDocExt(doc));

// ── Spreadsheet Viewer ───────────────────────────────────────────────────────
const SpreadsheetViewer = ({ url }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSheet, setActiveSheet] = useState(0);
  const [sheetNames, setSheetNames] = useState([]);
  const workbookRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(url);
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf);
        if (cancelled) return;
        workbookRef.current = wb;
        setSheetNames(wb.SheetNames);
        setActiveSheet(0);
        renderSheet(wb, 0);
      } catch {
        if (!cancelled) setError('No se pudo cargar la hoja de cálculo');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  const renderSheet = (wb, idx) => {
    const ws = wb.Sheets[wb.SheetNames[idx]];
    setHtml(XLSX.utils.sheet_to_html(ws, { id: 'xlsx-preview' }));
  };

  const handleSheetChange = (idx) => {
    setActiveSheet(idx);
    if (workbookRef.current) renderSheet(workbookRef.current, idx);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="error">{error}</Typography></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {sheetNames.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.5, px: 2, py: 1, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa', flexWrap: 'wrap' }}>
          {sheetNames.map((name, i) => (
            <Chip key={i} label={name} size="small" onClick={() => handleSheetChange(i)}
              color={activeSheet === i ? 'primary' : 'default'}
              variant={activeSheet === i ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      )}
      <Box sx={{
        flex: 1, overflow: 'auto', p: 1,
        '& table': { borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem' },
        '& td, & th': { border: '1px solid #ddd', px: 1, py: 0.5, whiteSpace: 'nowrap' },
        '& th': { bgcolor: '#f5f5f5', fontWeight: 600 },
        '& tr:hover td': { bgcolor: '#f0f7ff' },
      }}>
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </Box>
    </Box>
  );
};

// ── Preview Modal ─────────────────────────────────────────────────────────────
const PreviewModal = ({ doc, url, open, onClose, onDownload }) => {
  const name = doc?.file_original_name || doc?.name || 'Documento';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <InsertDriveFileOutlined sx={{ color: '#5d5d60' }} />
          <Typography variant="subtitle1" noWrap title={name}>{name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Tooltip title="Abrir en nueva pestaña">
            <IconButton size="small" onClick={() => window.open(url, '_blank')}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar">
            <IconButton size="small" onClick={onDownload}>
              <DownloadOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar">
            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
        {isImage(doc) ? (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#111', p: 2 }}>
            <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </Box>
        ) : isVideo(doc) ? (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#111' }}>
            <video src={url} controls style={{ maxWidth: '100%', maxHeight: '100%' }} />
          </Box>
        ) : isAudio(doc) ? (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <audio src={url} controls />
          </Box>
        ) : isSpreadsheet(doc) ? (
          <SpreadsheetViewer url={url} />
        ) : (
          <iframe src={url} title={name} width="100%" height="100%" style={{ border: 'none', display: 'block' }} />
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const ProceedingDocuments = ({ proceedingId, documents = [], onUpdate, isClosed = false, companies = [] }) => {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadingFolderIds, setUploadingFolderIds] = useState(new Set());
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [search, setSearch] = useState('');
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const fileInputRef = useRef(null);

  // Merge state
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeOrder, setMergeOrder] = useState([]);
  const [mergeDocs, setMergeDocs] = useState([]); // docs con metadata para el dialog
  const [mergeName, setMergeName] = useState('');
  const [merging, setMerging] = useState(false);
  const [uploadingMergeFile, setUploadingMergeFile] = useState(false);
  const mergeFileInputRef = useRef(null);

  // Edit state
  const [editDoc, setEditDoc] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Move state (single)
  const [moveDoc, setMoveDoc] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  // Move mode (batch)
  const [moveMode, setMoveMode] = useState(false);
  const [batchSelectedDocs, setBatchSelectedDocs] = useState([]);
  const [batchMoveDialogOpen, setBatchMoveDialogOpen] = useState(false);

  // AI Chat
  const [chatOpen, setChatOpen] = useState(false);

  // Signature state
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signDoc, setSignDoc] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signaturesDialogDoc, setSignaturesDialogDoc] = useState(null);
  const [signatureCounts, setSignatureCounts] = useState({});

  // Share document state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [shareSearchTerm, setShareSearchTerm] = useState('');
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [shareSearching, setShareSearching] = useState(false);
  const [shareSelectedUser, setShareSelectedUser] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [shareEntityModalOpen, setShareEntityModalOpen] = useState(false);
  const [shareCompanies, setShareCompanies] = useState([]);

  // Drag and drop
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);

  // Sort
  const { sorted: sortedDocuments, sortBy, sortDir, handleSort } = useSortTable(documents);

  useEffect(() => {
    loadFolders();
  }, [proceedingId]);

  useEffect(() => {
    if (folders.length > 0) {
      setExpandedFolders(new Set(folders.map(f => f.id.toString())));
    }
  }, [folders.length]);

  const loadFolders = async () => {
    try {
      const response = await proceedingService.getFolders(proceedingId);
      setFolders(response.data || []);
    } catch {
      // ignore gracefully
    }
  };

  useEffect(() => {
    const counts = {};
    documents.forEach(doc => {
      if (doc.signatureCount > 0) counts[doc.id.toString()] = doc.signatureCount;
    });
    setSignatureCounts(counts);
  }, [documents]);

  const refreshSignatureCounts = async () => {
    const counts = {};
    await Promise.all(
      documents.map(async (doc) => {
        try {
          const res = await proceedingService.getDocumentSignatures(proceedingId, doc.id);
          if (res.data?.length > 0) counts[doc.id.toString()] = res.data.length;
        } catch {}
      })
    );
    setSignatureCounts(counts);
  };

  const handleSignDocument = async () => {
    if (!signDoc) return;
    setSigning(true);
    try {
      await proceedingService.signDocument(proceedingId, signDoc.id);
      showSnackbar('Documento firmado exitosamente');
      setSignDialogOpen(false);
      setSignDoc(null);
      refreshSignatureCounts();
      if (onUpdate) onUpdate();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al firmar el documento', 'error');
    } finally {
      setSigning(false);
    }
  };

  // ─── Share document handlers ────────────────────────────────────────────

  const handleOpenShareDialog = (doc) => {
    setShareDoc(doc);
    setShareSearchTerm('');
    setShareSearchResults([]);
    setShareSelectedUser(null);
    setShareMessage('');
    setShareDialogOpen(true);
  };

  const handleShareSearch = async () => {
    if (!shareSearchTerm) return;
    setShareSearching(true);
    try {
      const response = await entityService.getAll({ search: shareSearchTerm, limit: 10 });
      setShareSearchResults(response.data || []);
    } catch {
      setShareSearchResults([]);
    } finally {
      setShareSearching(false);
    }
  };

  const handleConfirmShareDoc = async () => {
    if (!shareSelectedUser || !shareDoc) return;
    try {
      await proceedingService.shareDocumentWithUser(proceedingId, shareDoc.id, shareSelectedUser.id, shareMessage);
      showSnackbar('Documento compartido exitosamente');
      setShareDialogOpen(false);
    } catch (error) {
      showSnackbar(extractErrorMessage(error), 'error');
    }
  };

  const handleShareOpenEntityModal = async () => {
    if (shareCompanies.length === 0) {
      try {
        const res = await companyService.getAll({ limit: 100 });
        setShareCompanies(res.data);
      } catch {}
    }
    setShareEntityModalOpen(true);
  };

  const handleShareCreateEntity = async (data) => {
    const created = await entityService.create(data);
    setShareEntityModalOpen(false);
    setShareSelectedUser(created.data);
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      const key = folderId.toString();
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await proceedingService.createFolder(proceedingId, newFolderName.trim());
      showSnackbar('Carpeta creada exitosamente');
      setNewFolderName('');
      setNewFolderOpen(false);
      loadFolders();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al crear carpeta', 'error');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('¿Eliminar esta carpeta? Los documentos dentro quedarán sin carpeta.')) return;
    try {
      await proceedingService.deleteFolder(proceedingId, folderId);
      showSnackbar('Carpeta eliminada');
      loadFolders();
      if (onUpdate) onUpdate();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al eliminar carpeta', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchDocUrl = async (docId) => {
    const response = await documentService.getById(docId);
    const doc = response.data || response;
    if (!doc?.url) throw new Error('No se pudo obtener la URL del documento.');
    return doc.url;
  };

  const handleFileSelect = async (event, folderId = null) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    if (folderId) {
      setUploadingFolderIds(prev => new Set(prev).add(folderId.toString()));
    } else {
      setUploading(true);
    }
    
    setError('');
    let successCount = 0;
    let errorCount = 0;
    
    try {
      for (let i = 0; i < files.length; i++) {
        try {
          await proceedingService.uploadDocument(proceedingId, files[i], folderId);
          successCount++;
        } catch (err) {
          console.error('Error uploading', files[i].name, err);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        showSnackbar(
          files.length > 1
            ? `${successCount} documento(s) subido(s)${errorCount > 0 ? `, ${errorCount} con error` : ''}`
            : 'Documento subido correctamente'
        );
        if (onUpdate) onUpdate();
      } else {
        setError('Error al subir los documentos');
      }
    } finally {
      if (folderId) {
        setUploadingFolderIds(prev => {
          const next = new Set(prev);
          next.delete(folderId.toString());
          return next;
        });
      } else {
        setUploading(false);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePreview = async (doc) => {
    try {
      setPreviewLoading(true);
      const url = await fetchDocUrl(doc.id);
      setPreviewDoc(doc);
      setPreviewUrl(url);
    } catch (err) {
      showSnackbar(err.message, 'warning');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (docId) => {
    try {
      const url = await fetchDocUrl(docId);
      window.open(url, '_blank');
    } catch (err) {
      showSnackbar(err.message, 'warning');
    }
  };

  const handleDetach = async (documentId) => {
    if (!window.confirm('¿Desvincular este documento del expediente?')) return;
    try {
      await proceedingService.detachDocument(proceedingId, documentId);
      showSnackbar('Documento desvinculado');
      if (onUpdate) onUpdate();
    } catch (err) {
      showSnackbar(extractErrorMessage(err), 'error');
    }
  };

  const handleMoveDocument = async (targetFolderId) => {
    try {
      await proceedingService.moveDocument(proceedingId, moveDoc.id, targetFolderId);
      showSnackbar('Documento movido exitosamente');
      setMoveDialogOpen(false);
      setMoveDoc(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al mover el documento', 'error');
    }
  };

  const handleToggleBatchSelectDoc = (doc) => {
    setBatchSelectedDocs(prev => {
      const exists = prev.find(d => d.id === doc.id);
      if (exists) return prev.filter(d => d.id !== doc.id);
      return [...prev, doc];
    });
  };

  const handleCancelMoveMode = () => {
    setMoveMode(false);
    setBatchSelectedDocs([]);
  };

  const handleBatchMoveDocument = async (targetFolderId) => {
    try {
      await Promise.all(
        batchSelectedDocs.map(doc => proceedingService.moveDocument(proceedingId, doc.id, targetFolderId))
      );
      showSnackbar(`${batchSelectedDocs.length} documento(s) movido(s) exitosamente`);
      setBatchMoveDialogOpen(false);
      setMoveMode(false);
      setBatchSelectedDocs([]);
      if (onUpdate) onUpdate();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al mover los documentos', 'error');
    }
  };

  const handleEdit = (doc) => {
    setEditDoc({ ...doc, companyId: user?.companyId || doc.companyId });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (data) => {
    try {
      await documentService.update(editDoc.id, data);
      showSnackbar('Documento actualizado exitosamente');
      setEditDialogOpen(false);
      setEditDoc(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      throw error;
    }
  };

  const isPDF = (doc) => {
    return [doc.file_original_name, doc.name, doc.file]
      .filter(Boolean)
      .some(n => getFileExt(n) === 'pdf');
  };

  const handleToggleSelectDoc = (doc) => {
    if (!isPDF(doc)) {
      showSnackbar('Solo se pueden mezclar documentos PDF', 'warning');
      return;
    }
    setSelectedDocs(prev => {
      const exists = prev.find(d => d.id === doc.id);
      if (exists) {
        return prev.filter(d => d.id !== doc.id);
      } else {
        return [...prev, doc];
      }
    });
  };

  const handleStartMerge = () => {
    if (selectedDocs.length < 1) {
      showSnackbar('Selecciona al menos 1 documento PDF para mezclar', 'warning');
      return;
    }
    const initialDocs = selectedDocs.map(d => ({ id: d.id, name: d.file_original_name || d.name, isNew: false }));
    setMergeDocs(initialDocs);
    setMergeOrder(initialDocs.map(d => d.id));
    setMergeName('Documento_Mezclado.pdf');
    setMergeDialogOpen(true);
  };

  const handleAddMergeFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showSnackbar('Solo se pueden agregar archivos PDF', 'warning');
      return;
    }
    try {
      setUploadingMergeFile(true);
      const response = await proceedingService.uploadDocument(proceedingId, file);
      const newDoc = response.data;
      const entry = { id: newDoc.id, name: newDoc.file_original_name || newDoc.name || file.name, isNew: true };
      setMergeDocs(prev => [...prev, entry]);
      setMergeOrder(prev => [...prev, newDoc.id]);
    } catch (err) {
      showSnackbar('Error al subir el archivo', 'error');
    } finally {
      setUploadingMergeFile(false);
      if (mergeFileInputRef.current) mergeFileInputRef.current.value = '';
    }
  };

  const handleRemoveFromMerge = (id) => {
    setMergeDocs(prev => prev.filter(d => d.id !== id));
    setMergeOrder(prev => prev.filter(docId => docId !== id));
  };

  const handleCancelMerge = () => {
    setMergeMode(false);
    setSelectedDocs([]);
    setMergeDocs([]);
    setMergeOrder([]);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...mergeOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setMergeOrder(newOrder);
    const newDocs = [...mergeDocs];
    [newDocs[index - 1], newDocs[index]] = [newDocs[index], newDocs[index - 1]];
    setMergeDocs(newDocs);
  };

  const handleMoveDown = (index) => {
    if (index === mergeOrder.length - 1) return;
    const newOrder = [...mergeOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setMergeOrder(newOrder);
    const newDocs = [...mergeDocs];
    [newDocs[index], newDocs[index + 1]] = [newDocs[index + 1], newDocs[index]];
    setMergeDocs(newDocs);
  };

  const handleConfirmMerge = async () => {
    if (!mergeName.trim()) {
      showSnackbar('Ingresa un nombre para el documento mezclado', 'warning');
      return;
    }
    if (mergeOrder.length < 2) {
      showSnackbar('Se necesitan al menos 2 documentos para mezclar', 'warning');
      return;
    }
    try {
      setMerging(true);
      const response = await documentService.merge(mergeOrder, mergeName.trim());
      const mergedDoc = response.data;

      // Vincular el documento mezclado al expediente
      await proceedingService.attachDocument(proceedingId, mergedDoc.id);

      showSnackbar('Documentos mezclados exitosamente');
      setMergeDialogOpen(false);
      setMergeMode(false);
      setSelectedDocs([]);
      setMergeDocs([]);
      setMergeOrder([]);
      setMergeName('');
      if (onUpdate) onUpdate();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al mezclar documentos', 'error');
    } finally {
      setMerging(false);
    }
  };


  const matchesSearch = (doc) => {
    if (!search) return true;
    return (doc.file_original_name || doc.name || '').toLowerCase().includes(search.toLowerCase());
  };

  const SortCell = ({ label, field }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const docsInFolder = (folderId) =>
    sortedDocuments.filter(doc => doc.folderId === folderId.toString() && matchesSearch(doc));

  const docsWithoutFolder = sortedDocuments.filter(doc => !doc.folderId && matchesSearch(doc));

  const isUploadingFolder = (folderId) => uploadingFolderIds.has(folderId.toString());

  const handleDropUpload = async (e, folderId = null) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragOverFolderId(null);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;
    await handleFileSelect({ target: { files: droppedFiles } }, folderId);
  };

  const renderDocTable = (docs, emptyMsg) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {(mergeMode || moveMode) && <TableCell sx={{ width: 50 }}>SELECCIONAR</TableCell>}
            <SortCell label="Nombre" field="file_original_name" />
            <SortCell label="Fecha carga" field="createdAt" />
            <SortCell label="Fecha modificada" field="documentDate" />
            <TableCell align="right" sx={{ width: 140, whiteSpace: 'nowrap' }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {docs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={(mergeMode || moveMode) ? 5 : 4} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                {emptyMsg}
              </TableCell>
            </TableRow>
          ) : (
            docs.map((doc) => {
              const isSelected = selectedDocs.find(d => d.id === doc.id);
              const canSelect = isPDF(doc);
              return (
                <TableRow key={doc.id} hover selected={!!isSelected}>
                  {(mergeMode || moveMode) && (
                    <TableCell>
                      <Checkbox
                        checked={moveMode ? !!batchSelectedDocs.find(d => d.id === doc.id) : !!isSelected}
                        onChange={() => moveMode ? handleToggleBatchSelectDoc(doc) : handleToggleSelectDoc(doc)}
                        disabled={mergeMode && !canSelect}
                        size="small"
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InsertDriveFileOutlined sx={{ color: '#5d5d60' }} fontSize="small" />
                      <Typography variant="body2" fontWeight={500}>
                        {doc.name || doc.file_original_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {doc.createdAt ? new Date(doc.createdAt).toLocaleString('es-ES') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {doc.documentDate ? doc.documentDate.split('T')[0].split('-').reverse().join('/') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!mergeMode && (
                      <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                        {signatureCounts[doc.id.toString()] > 0 && (
                          <Tooltip title="Ver firmas">
                            <Chip
                              icon={<VerifiedUser sx={{ fontSize: 14 }} />}
                              label={`${signatureCounts[doc.id.toString()]} firma${signatureCounts[doc.id.toString()] > 1 ? 's' : ''}`}
                              size="small"
                              color="success"
                              variant="outlined"
                              onClick={() => setSignaturesDialogDoc(doc)}
                              sx={{ cursor: 'pointer', mr: 0.5 }}
                            />
                          </Tooltip>
                        )}
                        {hasPermission('proceeding.update') && !isClosed && (
                          <Tooltip title="Firmar documento">
                            <IconButton size="small" onClick={() => { setSignDoc(doc); setSignDialogOpen(true); }} sx={{ color: '#059669', width: 36, height: 36 }}>
                              <VerifiedUser sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('proceeding.update') && !isClosed && (
                          <Tooltip title="Compartir con usuario externo">
                            <IconButton size="small" onClick={() => handleOpenShareDialog(doc)} sx={{ color: '#5980a6', width: 36, height: 36 }}>
                              <ShareOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {doc.file && isPreviewable(doc) && (
                          <Tooltip title="Vista previa">
                            <IconButton size="small" onClick={() => handlePreview(doc)} disabled={previewLoading} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                              {previewLoading ? <CircularProgress size={16} /> : <VisibilityOutlined sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {doc.file && (
                          <Tooltip title="Descargar">
                            <IconButton size="small" onClick={() => handleDownload(doc.id)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                              <DownloadOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('document.update') && (
                          <Tooltip title="Ver detalle">
                            <IconButton size="small" onClick={() => navigate(`/documents/${doc.id}`)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                              <OpenInNew sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('document.update') && folders.length > 0 && !isClosed && !moveMode && (
                          <Tooltip title="Mover a carpeta">
                            <IconButton size="small" onClick={() => { setMoveDoc(doc); setMoveDialogOpen(true); }} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                              <DriveFileMove sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('document.delete') && (
                          <Tooltip title={isClosed ? "Expediente cerrado" : "Desvincular"}>
                            <span>
                              <IconButton size="small" onClick={() => handleDetach(doc.id)} disabled={isClosed} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                <DeleteOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Paper
      sx={{ p: 3, mb: 3, position: 'relative' }}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); }}
      onDrop={(e) => handleDropUpload(e)}
    >
      {isDragOver && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 10,
          bgcolor: 'rgba(89,128,166,0.06)',
          border: '2px dashed #5980a6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <Typography variant="h6" sx={{ color: '#5980a6' }}>Soltar archivos aquí</Typography>
        </Box>
      )}
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
          Documentos
          {(mergeMode || moveMode) && (
            <Chip
              label={`${mergeMode ? selectedDocs.length : batchSelectedDocs.length} seleccionados`}
              size="small"
              color="primary"
              sx={{ ml: 2 }}
            />
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {mergeMode ? (
            <>
              <Button
                variant="outlined"
                onClick={handleCancelMerge}
                size="small"
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<MergeType />}
                onClick={handleStartMerge}
                disabled={selectedDocs.length < 1}
                size="small"
              >
                Mezclar ({selectedDocs.length})
              </Button>
            </>
          ) : moveMode ? (
            <>
              <Button variant="outlined" onClick={handleCancelMoveMode} size="small">
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<DriveFileMove />}
                onClick={() => setBatchMoveDialogOpen(true)}
                disabled={batchSelectedDocs.length === 0}
                size="small"
              >
                Mover ({batchSelectedDocs.length})
              </Button>
            </>
          ) : (
            <>
              {documents.some(isPDF) && hasPermission('ai.chat') && (
                <Button
                  variant="outlined"
                  startIcon={<Chat />}
                  onClick={() => setChatOpen(true)}
                  size="small"
                >
                  Chatear con expediente
                </Button>
              )}
              {hasPermission('document.update') && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<MergeType />}
                    onClick={() => setMergeMode(true)}
                    size="small"
                    disabled={isClosed}
                  >
                    Mezclar PDFs
                  </Button>
                  {folders.length > 0 && !isClosed && (
                    <Button
                      variant="outlined"
                      startIcon={<DriveFileMove />}
                      onClick={() => setMoveMode(true)}
                      size="small"
                    >
                      Mover documentos
                    </Button>
                  )}
                  <Button
                    startIcon={<CreateNewFolder />}
                    onClick={() => setNewFolderOpen(true)}
                    size="small"
                    disabled={isClosed}
                  >
                    Nueva Carpeta
                  </Button>
                </>
              )}
              {hasPermission('document.create') && (
                <Button
                  component="label"
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                  disabled={uploading || isClosed}
                  size="small"
                >
                  {uploading ? 'Subiendo...' : isClosed ? 'Expediente cerrado' : 'Subir documento'}
                  <input
                    type="file"
                    multiple
                    hidden
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                  />
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <TextField
        size="small"
        placeholder="Buscar documentos..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 0.5 }} fontSize="small" /> }}
        sx={{ mb: 2, width: 300 }}
      />

      {/* Folders */}
      {folders.map((folder) => {
        const folderId = folder.id.toString();
        const isExpanded = expandedFolders.has(folderId);
        const folderDocs = docsInFolder(folder.id);

        return (
          <Paper key={folder.id} variant="outlined" sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                bgcolor: '#f2f2f3',
                borderRadius: 0,
                '&:hover': { bgcolor: 'rgba(29,31,32,0.07)' },
              }}
              onClick={() => toggleFolder(folder.id)}
            >
              {isExpanded
                ? <FolderOpen sx={{ color: '#F59E0B', mr: 1 }} />
                : <Folder sx={{ color: '#F59E0B', mr: 1 }} />}
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                {folder.name}
              </Typography>
              <Chip
                label={folderDocs.length}
                size="small"
                sx={{ mr: 1, bgcolor: 'rgba(29,31,32,0.08)', color: '#424244', fontWeight: 600 }}
              />
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              {hasPermission('document.delete') && (
                <Tooltip title={isClosed ? "Expediente cerrado" : "Eliminar carpeta"}>
                  <span onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                      onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                      disabled={isClosed}
                    >
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Collapse in={isExpanded}>
              <Box
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverFolderId(folder.id); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverFolderId(null); }}
                onDrop={(e) => { e.stopPropagation(); handleDropUpload(e, folder.id); }}
                sx={{ position: 'relative' }}
              >
                {dragOverFolderId === folder.id && (
                  <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 5,
                    bgcolor: 'rgba(89,128,166,0.06)',
                    border: '2px dashed #5980a6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                    <Typography variant="body1" sx={{ color: '#5980a6' }}>Soltar archivos aquí</Typography>
                  </Box>
                )}
                {renderDocTable(folderDocs, 'Carpeta vacía — agrega documentos usando el botón de abajo.')}
              </Box>
              {hasPermission('document.create') && (
                <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f0f0f0' }}>
                  <Button
                    component="label"
                    size="small"
                    startIcon={isUploadingFolder(folder.id) ? <CircularProgress size={14} /> : <Add />}
                    disabled={isUploadingFolder(folder.id) || isClosed}
                  >
                    {isUploadingFolder(folder.id) ? 'Subiendo...' : isClosed ? 'Expediente cerrado' : 'Agregar en esta carpeta'}
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={(e) => handleFileSelect(e, folder.id)}
                    />
                  </Button>
                </Box>
              )}
            </Collapse>
          </Paper>
        );
      })}

      {/* Documents without folder */}
      <Paper variant="outlined">
        <Box sx={{ px: 2, py: 1, bgcolor: '#f2f2f3' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {folders.length > 0 ? 'Sin carpeta' : 'Documentos'}
            <Chip
              label={docsWithoutFolder.length}
              size="small"
              sx={{ ml: 1, bgcolor: 'rgba(29,31,32,0.08)', color: '#424244', fontWeight: 600 }}
            />
          </Typography>
        </Box>
        {renderDocTable(docsWithoutFolder, 'No hay documentos. Usa "Cargar documentos" para subir uno.')}
      </Paper>

      {previewDoc && previewUrl && (
        <PreviewModal
          doc={previewDoc}
          url={previewUrl}
          open={Boolean(previewDoc && previewUrl)}
          onClose={() => { setPreviewDoc(null); setPreviewUrl(''); }}
          onDownload={() => handleDownload(previewDoc.id)}
        />
      )}

      {/* New Folder Dialog */}
      <Dialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nueva Carpeta</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Nombre de la carpeta"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNewFolderOpen(false); setNewFolderName(''); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onClose={() => !merging && setMergeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mezclar Documentos PDF</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre del documento mezclado"
            fullWidth
            value={mergeName}
            onChange={(e) => setMergeName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
            placeholder="Documento_Mezclado.pdf"
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              Orden de mezcla ({mergeOrder.length} documentos):
            </Typography>
            <Button
              component="label"
              size="small"
              startIcon={uploadingMergeFile ? <CircularProgress size={14} /> : <Add />}
              disabled={uploadingMergeFile || merging}
              variant="outlined"
            >
              {uploadingMergeFile ? 'Subiendo...' : 'Agregar PDF'}
              <input
                ref={mergeFileInputRef}
                type="file"
                hidden
                accept=".pdf"
                onChange={handleAddMergeFile}
              />
            </Button>
          </Box>

          <List dense>
            {mergeOrder.map((docId, index) => {
              const doc = mergeDocs.find(d => d.id === docId);
              if (!doc) return null;
              return (
                <ListItem
                  key={`${docId}-${index}`}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: doc.isNew ? '#F0FDF4' : '#fafafa',
                  }}
                >
                  <Chip
                    label={index + 1}
                    size="small"
                    color="primary"
                    sx={{ mr: 1, flexShrink: 0 }}
                  />
                  <ListItemText
                    primary={doc.name}
                    secondary={doc.isNew ? 'Nuevo' : null}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'success.main' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handleMoveUp(index)} disabled={index === 0}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleMoveDown(index)} disabled={index === mergeOrder.length - 1}>
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleRemoveFromMerge(docId)}>
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)} disabled={merging}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMerge}
            disabled={merging || !mergeName.trim() || mergeOrder.length < 2}
            startIcon={merging ? <CircularProgress size={16} /> : <MergeType />}
          >
            {merging ? 'Mezclando...' : 'Mezclar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Move Dialog */}
      <Dialog open={batchMoveDialogOpen} onClose={() => setBatchMoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mover {batchSelectedDocs.length} documento(s) a carpeta</DialogTitle>
        <DialogContent>
          <List dense>
            <ListItem
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => handleBatchMoveDocument(null)}
            >
              <ListItemText primary="Sin carpeta" secondary="Quitar de la carpeta actual" />
            </ListItem>
            {folders.map((folder) => (
              <ListItem
                key={folder.id}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => handleBatchMoveDocument(folder.id)}
              >
                <ListItemText primary={folder.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchMoveDialogOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Move Document Dialog */}
      <Dialog open={moveDialogOpen} onClose={() => { setMoveDialogOpen(false); setMoveDoc(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Mover documento a carpeta</DialogTitle>
        <DialogContent>
          <List dense>
            <ListItem
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => handleMoveDocument(null)}
            >
              <ListItemText primary="Sin carpeta" secondary="Quitar de la carpeta actual" />
            </ListItem>
            {folders.map((folder) => (
              <ListItem
                key={folder.id}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => handleMoveDocument(folder.id)}
              >
                <ListItemText primary={folder.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMoveDialogOpen(false); setMoveDoc(null); }}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      {editDoc && (
        <DocumentEditForm
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditDoc(null);
          }}
          document={editDoc}
          onSave={handleSaveEdit}
        />
      )}


      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Sign Document Dialog */}
      <Dialog open={signDialogOpen} onClose={() => !signing && setSignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedUser color="success" />
          Firmar documento
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Estás a punto de firmar electrónicamente el documento:
          </Typography>
          <Typography fontWeight={600} sx={{ mb: 2 }}>
            {signDoc?.file_original_name || signDoc?.name}
          </Typography>
          <Box sx={{ bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', p: 2, mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              {new Date().toLocaleString('es-ES')}
            </Typography>
          </Box>
          <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
            Al confirmar, se registrará su firma electrónica conforme al Decreto 2364 de 2012.
            Se capturarán datos de autenticación (IP, dispositivo) para garantizar la integridad del acto.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSignDialogOpen(false); setSignDoc(null); }} disabled={signing}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSignDocument}
            disabled={signing}
            startIcon={signing ? <CircularProgress size={16} color="inherit" /> : <VerifiedUser />}
          >
            {signing ? 'Firmando...' : 'Confirmar y firmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signatures Detail Dialog */}
      {signaturesDialogDoc && (
        <DocumentSignatures
          open={!!signaturesDialogDoc}
          onClose={() => setSignaturesDialogDoc(null)}
          proceedingId={proceedingId}
          documentId={signaturesDialogDoc.id}
          documentName={signaturesDialogDoc.file_original_name || signaturesDialogDoc.name}
        />
      )}

      <AiChatDialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        mode="proceeding"
        entityId={proceedingId}
        entityName="Expediente"
      />

      {/* Share Document Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {shareSelectedUser ? 'Confirmar compartir documento' : 'Compartir documento con usuario externo'}
        </DialogTitle>
        <DialogContent>
          {shareDoc && (
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              Documento: <strong>{shareDoc.file_original_name || shareDoc.name}</strong>
            </Alert>
          )}
          {!shareSelectedUser ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Nombre, email, DNI..."
                  value={shareSearchTerm}
                  onChange={(e) => setShareSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleShareSearch()}
                />
                <Button variant="contained" onClick={handleShareSearch} disabled={shareSearching}>
                  Buscar
                </Button>
                <Button variant="outlined" startIcon={<PersonAdd />} onClick={handleShareOpenEntityModal} sx={{ whiteSpace: 'nowrap' }}>
                  Nuevo
                </Button>
              </Box>
              <List>
                {shareSearchResults.map((user) => (
                  <ListItem key={user.id}>
                    <ListItemText
                      primary={`${user.name}${user.lastName ? ` ${user.lastName}` : ''}`}
                      secondary={user.email}
                    />
                    <Button size="small" variant="outlined" onClick={() => setShareSelectedUser(user)}>
                      Seleccionar
                    </Button>
                  </ListItem>
                ))}
                {shareSearchResults.length === 0 && shareSearchTerm && !shareSearching && (
                  <Typography align="center" color="text.secondary">
                    No se encontraron resultados
                  </Typography>
                )}
              </List>
            </>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Compartiendo con: <strong>{shareSelectedUser.name}{shareSelectedUser.lastName ? ` ${shareSelectedUser.lastName}` : ''}</strong> ({shareSelectedUser.email})
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Mensaje (opcional)"
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                placeholder="Escribe un mensaje opcional..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {shareSelectedUser ? (
            <>
              <Button onClick={() => setShareSelectedUser(null)}>Atrás</Button>
              <Button variant="contained" onClick={handleConfirmShareDoc}>Compartir</Button>
            </>
          ) : (
            <Button onClick={() => setShareDialogOpen(false)}>Cerrar</Button>
          )}
        </DialogActions>
      </Dialog>

      <EntityModalForm
        open={shareEntityModalOpen}
        onClose={() => setShareEntityModalOpen(false)}
        onSave={handleShareCreateEntity}
        entity={null}
        companies={shareCompanies}
      />
    </Paper>
  );
};

export default ProceedingDocuments;
